import { ListParams } from "../models/listParams";
import { Annotation } from "../models/portfolio/annotation";
import { Portfolio } from "../models/portfolio/portfolio";
import { Task } from "../models/portfolio/task";
import { PortfolioWebService } from "../webservices/portfolio.web.service";
import { BaseService } from "./base.service";
import { mapService } from "./map.service";
import { plannenService } from "./plannen.service";
import { retryWithAuth } from "./utils/retryWithAuth";

export class PortfolioService  extends BaseService {
  private static instance: PortfolioService;

  // Private constructor to prevent direct instantiation
  private constructor() {
    super();
  }

  public static getInstance(): PortfolioService {
    if (!PortfolioService.instance) {
      PortfolioService.instance = new PortfolioService();
    }
    return PortfolioService.instance;
  }

  private getService(): PortfolioWebService {
    return new PortfolioWebService();
  }
  
  // -----------------------
  // Inclusion Requests
  // -----------------------

  public async addPlanDeelnameToPortfolio(infplId: string, portfolioId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.postInclusionRequest(portfolioId, infplId, org.id), 2)
    );
  }

  public async deletePlanDeelnameVerzoek(inclusionRequestId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(
        () => service.deleteInclusionRequest(inclusionRequestId, org.id),
        2
      )
    );
  }

  public async updatePlanDeelnameStatus(status: string, inclusionRequestId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.putInclusionRequest(status, inclusionRequestId, org.id), 2)
    );
  }

  public async getPlanDeelnameVerzoekenHistory(inclusionRequestId: string): Promise<any> {
    const service = this.getService();

    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getInclusionRequestArchived(inclusionRequestId, org.id), 2)
    );
  }

  // -----------------------
  // Plan
  // -----------------------

  public async getPortfolioOverviewByPlan(
    infplId: string,
    params: ListParams
  ): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getPortfoliosByPlan(infplId, org.id, params), 2)
    );
  }

  // -----------------------
  // Portfolio
  // -----------------------

  public async createPortfolio(portfolio: Portfolio): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.postPortfolio(portfolio, org.id), 2)
    );
  }

  public async updatePortfolio(portfolio: Portfolio): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.putPortfolio(portfolio, org.id), 2)
    );
  }

  public async getPortfolios(
    params: ListParams
  ): Promise<any> {
    const service = this.getService();

    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getPortfolios(org.id, params), 2)
    );
  }

  public async getPortfolio(portfolioId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getPortfolio(portfolioId, org.id), 2)
    );
  }

  public async getPortfolioIncludingCalculatedBbox(portfolioId: string): Promise<any> {
    const service = this.getService();
    const organisatie = await this.getOrganisatieOrReject();
    const data = await retryWithAuth(() => service.getPortfolio(portfolioId, organisatie.id), 2) as Portfolio;
  
    if (!data || !Array.isArray(data.plans)) {
      return data;
    }

    const bboxes: number[][] = [];
  
    for (const plan of data.plans) {
      if (mapService.isValidBbox(plan?.boundingBox)) {
        bboxes.push(plan.boundingBox);
      }
    }

    const combinedBbox = bboxes.length > 0
      ? bboxes.reduce(
          (acc, bbox) => [
            Math.min(acc[0], bbox[0]),
            Math.min(acc[1], bbox[1]),
            Math.max(acc[2], bbox[2]),
            Math.max(acc[3], bbox[3]),
          ],
          [...bboxes[0]]
        )
      : null;
  
    data.bbox = combinedBbox as [number, number, number, number];
  
    return data;
  }

  public async getPortfolioInclPlanData(portfolioId: string): Promise<any> {
    const service = this.getService();
    const organisatie = await this.getOrganisatieOrReject();
    const data = await retryWithAuth(() => service.getPortfolio(portfolioId, organisatie.id), 2) as Portfolio;
  
    if (!data || !Array.isArray(data.plans)) {
      return data;
    }

    const detailedPlans: any[] = [];
    const bboxes: number[][] = [];
  
    // Detaildata ophalen per plan
    for (const plan of data.plans) {
      try {
        const detail = await plannenService.getPlan(plan.infplId);
  
        if (mapService.isValidBbox(detail?.bbox)) {
          bboxes.push(detail.bbox);
        }
  
        detailedPlans.push({
          ...plan,
          detail,
        });
  
      } catch (err: any) {
        if (err.status === 404) {
          detailedPlans.push({
            ...plan,
          });
          // Skip silently
          continue;
        }
        else {
          throw err; // Rethrow non-404 errors
        }
      }
    }

    const combinedBbox = bboxes.length > 0
      ? bboxes.reduce(
          (acc, bbox) => [
            Math.min(acc[0], bbox[0]),
            Math.min(acc[1], bbox[1]),
            Math.max(acc[2], bbox[2]),
            Math.max(acc[3], bbox[3]),
          ],
          [...bboxes[0]]
        )
      : null;
  
    data.plans = detailedPlans;
    data.bbox = combinedBbox as [number, number, number, number];
  
    return data;
  }
  
  public async getPortfolioArchived(portfolioId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getPortfolioArchived(portfolioId, org.id), 2)
    );
  }

  public async getConsortiums(params?: ListParams): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getConsortiums(org.id, params), 2)
    );
  }

  public async getConsortium(consortiumId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getConsortium(org.id, consortiumId), 2, { redirectOnForbidden: false })
    ).catch((error: any) => {
      if (this.isForbiddenError(error)) {
        console.warn('[PortfolioService] 403 ontvangen voor de huidige rol bij GET /api/v1/consortiums; fallback naar lege lijst.');
        return [];
      }

      throw error;
    });
  }

  public async addConsortiumToPortfolio(portfolioId: string, consortiumId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.addConsortiumToPortfolio(portfolioId, consortiumId, org.id), 2)
    );
  }

  public async removeConsortiumFromPortfolio(portfolioId: string, consortiumId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.removeConsortiumFromPortfolio(portfolioId, consortiumId, org.id), 2)
    );
  }

  // -----------------------
  // Annotations
  // -----------------------

  public async postAnnotation(annotation: Annotation): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.postAnnotation(annotation, org.id), 2)
    );
  }

  public async putAnnotation(annotation: Annotation): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.putAnnotation(annotation, org.id), 2)
    );
  }

  public async getAnnotationChildren(
    annotationId: string,
    params: ListParams
  ): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getAnnotationChildren(annotationId, org.id, params), 2)
    );
  }

  public async getAnnotationsByPortfolio(
    portfolioId: string,
    params: ListParams
  ): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getAnnotationsByPortfolio(portfolioId, org.id, params), 2)
    );
  }

  public async getAnnotationsByTask(
    taskId: string,
    params: ListParams
  ): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getAnnotationsByTask(taskId, org.id, params), 2)
    );
  }
  
  public async getAnnotationsArchived(annotationId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getAnnotationsArchived(annotationId, org.id), 2)
    );
  }

  // -----------------------
  // Tasks
  // -----------------------

  public async postTask(task: Task): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.postTask(task, org.id), 2)
    );
  }

  public async putTask(task: Task): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.putTask(task, org.id), 2)
    );
  }

  public async getTasksByPortfolio(
    portfolioId: string,
    params: ListParams
  ): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getTasksByPortfolio(portfolioId, org.id, params), 2)
    );
  }
  
  public async getTasksArchived(taskId: string): Promise<any> {
    const service = this.getService();
    return this.getOrganisatieOrReject().then(org =>
      retryWithAuth(() => service.getTasksArchived(taskId, org.id), 2)
    );
  }
}

export const portfolioService = PortfolioService.getInstance();
